import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate } from '../utils/dateFormatter';
import './GraphicRecapSplitScreen.css';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function GraphicRecapSplitScreen({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dateFormat = 'full',
}) {
  const {
    homeTeam,
    homeScore,
    awayTeam,
    awayScore,
    gameDate,
    topPerformer,
    topPerformerStats,
  } = graphicData;

  const homeWon = parseInt(homeScore) > parseInt(awayScore);
  const isTie = parseInt(homeScore) === parseInt(awayScore);

  // Generate background based on style
  const getBackgroundStyle = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(180deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(180deg, ${teamColors.primary} 0%, ${teamColors.primary} 50%, ${teamColors.secondary} 50%, ${teamColors.secondary} 100%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(180deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  const isLightColor = (color) => {
    if (!color || color === 'null') return false; // Default to dark if color is null/undefined
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  };

  const getTextColor = (bgColor) => {
    return isLightColor(bgColor) ? '#1a202c' : '#ffffff';
  };

  return (
    <div className="graphic-recap-split" id="graphic-recap">
      {/* Left Side - Score & Badge */}
      <div
        className="recap-split-left"
        style={{ background: getBackgroundStyle() }}
      >
        <div
          className="recap-split-badge-top"
          style={{
            background: isLightColor(teamColors.primary)
              ? 'rgba(0, 0, 0, 0.15)'
              : 'rgba(255, 255, 255, 0.2)',
            color: getTextColor(teamColors.primary),
          }}
        >
          {isTie ? 'TIE GAME' : homeWon ? 'VICTORY!' : 'FINAL SCORE'}
        </div>

        <div className="recap-split-score-section">
          <div className="recap-split-score-large">
            {homeScore || '0'} - {awayScore || '0'}
          </div>

          <div className="recap-split-logos">
            {/* Home Team Logo */}
            <div className="recap-split-logo">
              {homeTeamLogo ? (
                <div className="team-logo-circle team-logo-circle-lg">
                  <img
                    src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
                    alt={homeTeam}
                  />
                </div>
              ) : (
                <div className="team-logo-circle team-logo-circle-lg">
                  <div className="recap-logo-placeholder">
                    {teamSport === 'Softball' ? '🥎' : '⚾'}
                  </div>
                </div>
              )}
            </div>

            {/* VS Divider */}
            <div className="recap-split-vs-divider">VS</div>

            {/* Away Team Logo */}
            <div className="recap-split-logo">
              {awayTeamLogoPreview ? (
                <div className="team-logo-circle team-logo-circle-lg">
                  <img src={awayTeamLogoPreview} alt={awayTeam} />
                </div>
              ) : (
                <div className="team-logo-circle team-logo-circle-lg">
                  <div className="recap-logo-placeholder">
                    {teamSport === 'Softball' ? '🥎' : '⚾'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Info */}
      <div className="recap-split-right">
        <div className="recap-split-teams">
          <div
            className="recap-split-team-name home"
            style={{ color: teamColors.primary }}
          >
            {homeTeam || 'YOUR TEAM'}
          </div>
          <div className="recap-split-vs">VS</div>
          <div
            className="recap-split-team-name away"
            style={{
              color: isLightColor(teamColors.secondary)
                ? isLightColor(teamColors.primary)
                  ? '#2d3748'
                  : teamColors.primary
                : teamColors.secondary,
            }}
          >
            {awayTeam || 'OPPONENT'}
          </div>
        </div>

        {topPerformer && (
          <div className="recap-split-performer">
            <div className="performer-label">⭐ PLAYER OF THE GAME</div>
            <div className="performer-name">{topPerformer}</div>
            {topPerformerStats && (
              <div className="performer-stats">{topPerformerStats}</div>
            )}
          </div>
        )}

        {gameDate && (
          <div className="recap-split-date">{formatGameDate(gameDate, dateFormat)}</div>
        )}

        {showWatermark && (
          <div className="playmaker-watermark">
            <img src="/PMlogoleft.png" alt="Playmaker Media Kit" />
          </div>
        )}
      </div>
    </div>
  );
}

export default GraphicRecapSplitScreen;
